import { Input } from '@/components/ui/input';
import { useSearch } from '@/hooks/useSearch';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import MyLiveSearch from '../SearchTest/MyLiveSearchBox';
import { products } from '@/containers/Products/ProductsArray';
import ResponsiveTitle from '@/components/ResponsiveTitle';
import { motion } from 'framer-motion';
import logo from '/iconlogo.svg';
import logoSmall from '@/assets/images/logo.svg';
import { usePageLoading } from '@/hooks/usePageLoading';

function NavbarOld() {
  const searchContext = useSearch();
  const { setLoading } = usePageLoading();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [results, setResults] = useState<(typeof products)[number][]>();
  const [selectedProduct, setSelectedProduct] = useState<(typeof products)[number]>();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;
    const trimmedValue = value.trim().toLowerCase();
    if (!trimmedValue) return setResults([]);
    const filteredValue = products.filter((product) =>
      product.cardTitle.toLowerCase().includes(trimmedValue)
    );
    setResults(filteredValue);
  };

  const handleNavigation = (targetPath: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Prevent navigation if we're already on the target page
    if (location.pathname === targetPath) {
      e.preventDefault();
      setIsDropdownOpen(false);
      return;
    }
    
    setLoading(true);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    checkTouch();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setClickedIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const listItemVariants = {
    hidden: { opacity: 0, y: 5 },
    visible: { opacity: 1, y: 0 },
  };

  const routesMobile = ['/', '/about', '/products', '/services', '/projects', '/gallery', '/contact'];
  const labelsMobile = ['Home', 'About Us', 'Products', 'Services', 'Our Projects', 'Gallery', 'Contact Us'];

  const routesDesktop = ['/', '/about', '/services', '/products', '/projects', '/gallery', '/contact'];
  const labelsDesktop = ['Home', 'About Us', 'Services', 'Products', 'Our Projects', 'Gallery', 'Contact Us'];

  return (
    <nav id='navbar' className='z-40 fixed w-full text-white pb-20'>
      <div className='navbar bg-custom-darkblue'>
        <div className='navbar-start'>

          {/* Mobile Dropdown */}
          <div
            ref={dropdownRef}
            className="relative group lg:hidden"
            onMouseEnter={() => {
              if (!isTouchDevice) setIsDropdownOpen(true);
            }}
            onMouseLeave={() => {
              if (!isTouchDevice) setIsDropdownOpen(false);
            }}
          >
            <div
              className="btn btn-ghost hover:btn-white hover:bg-custom-hov"
              onClick={() => {
                if (isTouchDevice) setIsDropdownOpen((prev) => !prev);
              }}
            >
              <svg xmlns='http://www.w3.org/2000/svg' className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='white'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M4 6h16M4 12h8m-8 6h16' />
              </svg>
            </div>

            <ul
              className={`menu menu-sm dropdown-content p-2 shadow bg-custom-darkblue rounded-lg w-60 absolute top-full mt-[1.1rem] transition-all duration-200 ${
                isDropdownOpen ? 'block' : 'hidden'
              }`}
            >
                          {routesMobile.map((path, i) => (
              <motion.li
                key={path}
                variants={listItemVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.3, delay: 0.1 }}
                whileTap={{ scale: 0.97 }}
              >
                <NavLink
                  to={path}
                  className={({ isActive }) =>
                    `hover:text-white hover:bg-custom-hov px-3 py-1 rounded-sm ${
                      isActive ? 'bg-[#44b388] text-white font-semibold' : 'text-white'
                    }`
                  }
                  onClick={handleNavigation(path)}
                >
                  {labelsMobile[i]}
                </NavLink>
              </motion.li>
            ))}
          </ul>
          </div>

          {/* Logo + Title */}
          {/* Logo + Title */}
          <figure className="flex items-center justify-center">
            {/* Small screen logo only visible on small screens */}
            <img
              src={logoSmall}
              alt="Sheel Waterproofing logo"
              className="w-[clamp(70px,20vw,100px)] h-[60px] object-contain block lg:hidden ml-2"
            />
            {/* Large screen logo visible only on large screens */}
            <img
              src={logo}
              alt="Sheel Waterproofing logo"
              className="w-[clamp(65px,18vw,100px)] h-[50px] object-contain hidden lg:block lg:pl-8"
            />
          </figure>

          <NavLink to='' className='hidden lg:block' onClick={handleNavigation('/')}>
            <ResponsiveTitle className="btn btn-ghost no-shadow-on-hover group leading-tight text-white whitespace-nowrap">
              Sheel <span className="group-hover:text-custom-hov">Waterproofing</span>
            </ResponsiveTitle>
          </NavLink>
        </div>

        {/* Desktop Navbar */}
        <div className='navbar-center hidden lg:flex z-50'>
          <motion.ul
            className='menu menu-horizontal gap-1'
            initial='hidden'
            animate='visible'
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.07 } },
            }}
          >
            {routesDesktop.map((path, i) => (
              <motion.li
                key={path}
                variants={listItemVariants}
                onHoverStart={() => setHoveredIndex(i)}
                onHoverEnd={() => setHoveredIndex(null)}
                animate={
                  hoveredIndex === null
                    ? { scale: 1, opacity: 1 }
                    : hoveredIndex === i
                    ? { scale: 1.1, opacity: 1 }
                    : { scale: 0.95, opacity: 0.8 }
                }
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <NavLink
                  to={path}
                  className={({ isActive }) => {
                    const base = 'relative px-3 lg:px-4 py-1 rounded-lg transition-all duration-200';
                    const active = 'bg-[#44b388] text-white';
                    const hoverStyle = 'hover:bg-custom-hov hover:text-white hover:shadow-lg';

                    const isHovered = hoveredIndex === i;
                    const isHoveredOther = hoveredIndex !== null && hoveredIndex !== i;
                    const shouldFadeActive = isActive && !isHovered && scrollY > 20;
                    const scaleStyle = isActive && isHoveredOther ? 'scale-[0.97]' : '';

                    return `${base} ${isActive ? active : ''} ${
                      !isActive ? 'hover:shadow-lg' : 'shadow-none'
                    } ${shouldFadeActive ? 'opacity-50' : 'opacity-100'} ${scaleStyle} ${hoverStyle}`;
                  }}
                  onClick={handleNavigation(path)}
                >
                  <span className='relative z-10 font-white'>
                    {labelsDesktop[i]}
                    <motion.span
                      className='absolute bottom-0 left-0 w-full h-[1px] bg-white origin-left'
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: hoveredIndex === i ? 1 : 0 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      style={{ transformOrigin: 'left', display: 'block' }}
                    />
                  </span>
                </NavLink>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        {/* Search bar */}
        <div className="navbar-end flex pr-2 sm:pr-4 lg:mr-8 min-w-[150px]">
          <div className="w-full max-w-[20rem]">
            <MyLiveSearch
              results={results}
              value={selectedProduct?.cardTitle}
              renderItem={(item) => <p>{item.cardTitle}</p>}
              onChange={handleChange}
              onSelect={(item) =>
                setSelectedProduct({
                  ...item,
                  imageFileName: "imgnotavailable.png",
                })
              }
            />
          </div>
        </div>
      </div>
    </nav>
  );
}

export default NavbarOld;
